import { formatCountdownDuration } from "../../utils/matchCountdown";
import type { UserGame } from "../../Data/UserContext";
import { formatOddsForDisplay } from "../Trader/oddsUtils";
import { HISTORY_CACHE_KEY_PREFIX, HISTORY_CACHE_TTL_MS, QUICKFIRE_MONTHS_UPPER, QUICKFIRE_SUMMARY_WEEKDAYS } from "./constants";
import type {
    CombinedGame,
    QuickfirePlayRow,
    QuickfireQuestionParsed,
    SquareGame,
    TradeGame,
    BullionGame,
    SixesCard,
} from "./types";

export function formatOverUnderDisplay(str: string | undefined): string {
    if (!str || typeof str !== "string") return str ?? "";
    return str.replace(/\bOVER\b/g, "Over").replace(/\bUNDER\b/g, "Under");
}

/** Decimal odds for My Zone trader summary pill — always two fractional digits (e.g. 4 → 4.00). Fractional style e.g. `5/1` is left unchanged. */
export function formatTraderOddsTwoDecimals(raw: string | number | null | undefined): string {
    if (raw == null || raw === "") return "";
    const s = String(raw).trim();
    if (s.includes("/")) return s;
    return formatOddsForDisplay(raw) || s;
}

export function firstSegmentByComma(str: string | undefined): string {
    if (!str || typeof str !== "string") return str?.trim() || "Match";
    const first = str.split(",")[0]?.trim();
    return first || "Match";
}

export function secondSegmentByComma(str: string | undefined): string | undefined {
    if (!str || typeof str !== "string") return undefined;
    const parts = str.split(",").map((p) => p.trim()).filter(Boolean);
    return parts[1];
}

export function parseSquaresChallengeFromEntityRef(ref: unknown): { matchId: string | null; matchName: string | null } {
    if (typeof ref !== "string" || !ref.trim()) return { matchId: null, matchName: null };
    try {
        const parsed = JSON.parse(ref) as { match_id?: unknown; match_name?: unknown };
        if (parsed?.match_id == null) return { matchId: null, matchName: null };
        const matchId = String(parsed.match_id).trim() || null;
        const rawName = parsed?.match_name;
        const matchName =
            rawName != null && String(rawName).trim() !== "" ? String(rawName).trim() : null;
        return { matchId, matchName };
    } catch {
        return { matchId: null, matchName: null };
    }
}

export function parseSquaresChallengeMatchIdFromEntityRef(ref: unknown): string | null {
    return parseSquaresChallengeFromEntityRef(ref).matchId;
}

export function numAmount(v: unknown): number {
    if (v == null || v === "") return 0;
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

export function formatTradeTypeLabelForDisplay(
    label: string,
    marketdetails: TradeGame["marketdetails"] | undefined
): string {
    if (!label || !marketdetails?.marketname) return label ?? "";
    if (marketdetails.marketname.toLowerCase() === "correct score") return "Correct Score";
    return label;
}

export function traderUrlSportParam(sportLower: string): string {
    if (sportLower === "horse") return "horses";
    if (sportLower === "soccer") return "football";
    return sportLower;
}

export function buildFinishedTraderInitialEvent(trade: TradeGame): Record<string, unknown> {
    const label = firstSegmentByComma(trade.name || trade.marketdetails?.eventname) || "Match";
    const splitV = label.split(/\s+v\s+/i);
    const sideA = splitV[0]?.trim() || label;
    const sideB = splitV[1]?.trim() || "";
    const participants = sideB
        ? [{ "participant-name": sideA }, { "participant-name": sideB }]
        : [{ "participant-name": sideA }, { "participant-name": "" }];
    const ls =
        trade.liveScore && typeof trade.liveScore === "object" && !Array.isArray(trade.liveScore)
            ? (trade.liveScore as Record<string, unknown>)
            : {};
    return {
        id: trade.match_id,
        name: label,
        liveScore: { isFinished: true, ...ls },
        "event-participants": participants,
    };
}

export function quickfireOrdinal(n: number): string {
    const v = n % 100;
    if (v >= 11 && v <= 13) return "TH";
    switch (v % 10) {
        case 1:
            return "ST";
        case 2:
            return "ND";
        case 3:
            return "RD";
        default:
            return "TH";
    }
}

export function formatQuickfireHeroLine(
    playedAt: string,
    competitions: string | null,
    playName?: string | null
): string {
    const backendName = playName?.trim();
    if (backendName) {
        return (backendName.length > 52 ? `${backendName.slice(0, 49)}…` : backendName).toUpperCase();
    }
    const c = competitions?.trim();
    if (c) {
        return (c.length > 52 ? `${c.slice(0, 49)}…` : c).toUpperCase();
    }
    const d = new Date(playedAt);
    if (isNaN(d.getTime())) return "QUICKFIRE";
    const day = d.getDate();
    return `${QUICKFIRE_MONTHS_UPPER[d.getMonth()]} ${day}${quickfireOrdinal(day)}`;
}

export function formatQuickfireSummaryDateBar(playedAt: string, playDateLabel?: string | null): string {
    const backendLabel = playDateLabel?.trim();
    if (backendLabel) return backendLabel.toUpperCase();
    const d = new Date(playedAt);
    if (isNaN(d.getTime())) return "—";
    const wd = QUICKFIRE_SUMMARY_WEEKDAYS[d.getDay()];
    const day = d.getDate();
    const month = QUICKFIRE_MONTHS_UPPER[d.getMonth()];
    return `${wd} ${day}${quickfireOrdinal(day)} ${month}`;
}

const QUICKFIRE_WEEKDAYS_TITLE = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
] as const;

const QUICKFIRE_MONTHS_TITLE = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
] as const;

/** My Zone settled Quickfire card title — e.g. `Tuesday, 12th April` (local date, sentence-style). */
export function formatQuickfireMyZonePastSessionTitle(playedAt: string): string {
    const d = new Date(playedAt);
    if (Number.isNaN(d.getTime())) return "Quickfire";
    const wd = QUICKFIRE_WEEKDAYS_TITLE[d.getDay()];
    const day = d.getDate();
    const ord = quickfireOrdinal(day).toLowerCase();
    const month = QUICKFIRE_MONTHS_TITLE[d.getMonth()];
    return `${wd}, ${day}${ord} ${month}`;
}

/** Short local date for My Zone PAST row cards (D-M-YYYY, e.g. 12-5-2026). */
export function formatMyGamesPastCardDate(raw: string | number | Date | null | undefined): string | null {
    if (raw == null || raw === "") return null;
    const d = raw instanceof Date ? raw : new Date(typeof raw === "number" ? raw : String(raw));
    if (Number.isNaN(d.getTime())) return null;
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
}

export function parseQuickfireQuestions(play: QuickfirePlayRow): QuickfireQuestionParsed[] {
    const raw = play.questions;
    let arr: unknown[] = [];
    if (raw == null) {
        arr = [];
    } else if (Array.isArray(raw)) {
        arr = raw;
    } else if (typeof raw === "string") {
        try {
            const p = JSON.parse(raw) as unknown;
            arr = Array.isArray(p) ? p : [];
        } catch {
            arr = [];
        }
    }
    return arr.map((item) => {
        const o = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
        return {
            correct: o.correct === true,
            answered: o.answered === true,
            question: typeof o.question === "string" ? o.question : "",
            questionId: typeof o.questionId === "number" ? o.questionId : undefined,
        };
    });
}

export function quickfireSummaryCorrectCount(play: QuickfirePlayRow, parsed: QuickfireQuestionParsed[]): number {
    const fromRow = Number(play.correct);
    if (Number.isFinite(fromRow) && fromRow >= 0) {
        return Math.max(0, Math.floor(fromRow));
    }
    if (parsed.length > 0) return parsed.filter((q) => q.correct).length;
    return 0;
}

export function quickfireSummaryQuestionTotal(play: QuickfirePlayRow, parsed: QuickfireQuestionParsed[]): number {
    if (parsed.length > 0) return parsed.length;
    return Math.max(0, Number(play.max_questions) || 0);
}

export function normalizeOverWinnerRow(w: {
    play_id?: number;
    user_id?: number | null;
    rank?: number;
    avatar?: string | null;
    profile_pic?: string | null;
    name?: string;
    winnings?: number;
    return?: number;
}) {
    return { ...w, avatar: w.avatar ?? w.profile_pic ?? null };
}

/**
 * Same numeric value as backend `WINNINGS_TIE_EPSILON` (`backend/controllers/squares/squares-constants.js`).
 *
 * Squares scores (`winnings`) are floats; we treat two values as **tied** when their difference is at most
 * this amount, so client-side sort + competition place match the server (sorting, tie groups, skipped ranks).
 */
const SQUARES_WINNINGS_TIE_EPS = 1e-6;

/**
 * Integer chip total from API `winnings` (cent-round then whole chips; matches leaderboard UI).
 * Used so **0 chips** means no numeric rank (show "-" in rank badges / skip ordinal place).
 */
export function squaresLeaderboardChipInt(total: unknown): number {
    const num = Number(total);
    if (!Number.isFinite(num)) return 0;
    const toCents = Math.round(num * 100) / 100;
    return Math.round(toCents);
}

export function sortSquaresLeaderboardByPerformance(winners: NonNullable<SquareGame["winners"]>) {
    return [...winners].sort((a, b) => {
        const diff = (b.winnings ?? 0) - (a.winnings ?? 0);
        if (Math.abs(diff) > SQUARES_WINNINGS_TIE_EPS) return diff > 0 ? 1 : -1;
        const pa = a.play_id != null ? Number(a.play_id) : 0;
        const pb = b.play_id != null ? Number(b.play_id) : 0;
        return pa - pb;
    });
}

/** 1-based competition place for the row at `idx` in `ordered` (best-first, same tie epsilon as backend). */
function squaresCompetitionPlaceAtIndex(
    ordered: NonNullable<SquareGame["winners"]>,
    idx: number
): number {
    let nextRank = 1;
    let i = 0;
    while (i < ordered.length) {
        const score = ordered[i].winnings ?? 0;
        let j = i + 1;
        while (
            j < ordered.length &&
            Math.abs((ordered[j].winnings ?? 0) - score) <= SQUARES_WINNINGS_TIE_EPS
        ) {
            j++;
        }
        if (idx >= i && idx < j) return nextRank;
        nextRank += j - i;
        i = j;
    }
    return idx + 1;
}

export function findSquaresOrdinalPlace(
    playId: number | string | undefined,
    winners: NonNullable<SquareGame["winners"]> | null | undefined,
    userId?: number | string | null
): number | null {
    if (!winners?.length || playId == null) return null;
    const sid = String(playId);
    const ordered = sortSquaresLeaderboardByPerformance(winners);
    let idx = ordered.findIndex((w) => w.play_id != null && String(w.play_id) === sid);
    if (idx < 0 && userId != null && String(userId) !== "") {
        idx = ordered.findIndex((w) => w.user_id != null && String(w.user_id) === String(userId));
    }
    if (idx < 0) return null;
    if (squaresLeaderboardChipInt(ordered[idx].winnings) <= 0) return null;
    return squaresCompetitionPlaceAtIndex(ordered, idx);
}

export function getHistoryCacheKey(activeTab: string, gameTypeFilter: string) {
    return `${HISTORY_CACHE_KEY_PREFIX}_${activeTab}_${gameTypeFilter}`;
}

/** Parse `bullion_match.start_time` / my-games `startTime` to epoch ms, or null if missing/invalid. */
export function parseBullionMatchStartTimeMs(startTime: string | null | undefined): number | null {
    if (startTime == null || String(startTime).trim() === "") return null;
    const t = new Date(startTime).getTime();
    return Number.isFinite(t) ? t : null;
}

/**
 * True while the fixture should not show as “Match Live” in My Zone: kickoff is strictly in the future,
 * or start time is missing/invalid. Matches BullionMatchLanding (`startsAtMs == null` → kickoff not passed)
 * and BullionPlayedGameView’s future `scheduledStartTs` → scheduled branch.
 */
export function isBullionMatchFixtureNotStartedYet(
    startTime: string | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    const ms = parseBullionMatchStartTimeMs(startTime);
    if (ms == null) return true;
    return nowMs < ms;
}

/**
 * My Zone BULLION pre-kickoff footer — `Starts in 3H 40M` (white body type via caller classes), or em dash when unknown.
 */
export function formatBullionMatchKickoffFooterLabel(startTime: string | null | undefined, nowMs: number): string {
    const ms = parseBullionMatchStartTimeMs(startTime);
    if (ms == null) return "—";
    const duration = formatCountdownDuration(ms - nowMs, "upper");
    if (!duration) return "—";
    return `Starts in ${duration}`;
}

/** True when all pick lines are settled by count or the play row is marked settled (My Zone ACTIVE vs PAST). */
export function isBullionPlayFullyDone(
    bullionPlay: Pick<BullionGame, "settled" | "settledMarkets" | "totalMarkets"> | BullionGame
): boolean {
    if (bullionPlay.settled === true) return true;
    const total = Number(bullionPlay.totalMarkets ?? 0);
    const done = Number(bullionPlay.settledMarkets ?? 0);
    return total > 0 && done >= total;
}

/** True when all cheer cells are settled or the card row is marked settled/cancelled (My Zone ACTIVE vs PAST). */
export function isSixesCardFullyDone(
    card: Pick<SixesCard, "status" | "settledCells" | "totalCells"> | SixesCard,
): boolean {
    const status = String(card.status || "").trim().toLowerCase();
    if (status === "settled" || status === "cancelled") return true;
    const total = Number(card.totalCells ?? 0);
    const done = Number(card.settledCells ?? 0);
    return total > 0 && done >= total;
}

/** True when a Sixes card was fully voided (admin or natural all-VOID). */
export function isSixesCardVoided(
    card: Pick<SixesCard, "status"> | SixesCard | null | undefined,
): boolean {
    return String(card?.status || "").trim().toLowerCase() === "cancelled";
}

export function isGameActive(game: CombinedGame): boolean {
    if (game.type === "trader") {
        return (game.data as TradeGame).settled !== true;
    }
    if (game.type === "quickfire") {
        return !(game.data as QuickfirePlayRow).session_complete;
    }
    if (game.type === "bullion") {
        return !isBullionPlayFullyDone(game.data as BullionGame);
    }
    if (game.type === "sixes") {
        return !isSixesCardFullyDone(game.data as SixesCard);
    }
    const s = game.data as SquareGame;
    if ((s as { settled?: boolean }).settled === true) return false;
    if (["WIN", "LOSS", "LOST", "VOID"].includes((s as { outcome?: string }).outcome || "")) return false;
    return true;
}

export function isGameSettled(game: CombinedGame): boolean {
    if (game.type === "trader") {
        return (game.data as TradeGame).settled === true;
    }
    if (game.type === "quickfire") {
        const q = game.data as QuickfirePlayRow;
        return q.session_complete === true || !isTodayUtc(q.played_at);
    }
    if (game.type === "bullion") {
        return isBullionPlayFullyDone(game.data as BullionGame);
    }
    if (game.type === "sixes") {
        return isSixesCardFullyDone(game.data as SixesCard);
    }
    const s = game.data as SquareGame;
    return (s as { settled?: boolean }).settled === true;
}

/**
 * True when a settled row should count as “today” for My Games drawer / PAST ordering:
 * any match kickoff, in-play, settlement, or play timestamp falls on the local calendar day,
 * not only when the row was created.
 */
export function isSettledGameMatchOrActivityTodayLocal(game: CombinedGame): boolean {
    if (!isGameSettled(game)) return false;
    if (game.type === "trader") {
        const t = game.data as TradeGame;
        const mdStart = t.marketdetails?.start;
        const cands: (string | undefined | null)[] = [
            t.tstamp,
            t.created_at,
            (t as { "starts-at"?: string })["starts-at"],
            t.start,
            mdStart != null && mdStart !== "" ? String(mdStart) : undefined,
        ];
        return cands.some((d) => typeof d === "string" && d.trim() !== "" && isTodayLocal(d));
    }
    if (game.type === "squares") {
        const s = game.data as SquareGame;
        const cands: (string | undefined | null)[] = [
            s.settled_at,
            s.actual_start,
            s.start_time,
            s.start,
            s.created_at,
        ];
        return cands.some((d) => typeof d === "string" && d.trim() !== "" && isTodayLocal(d));
    }
    if (game.type === "quickfire") {
        const q = game.data as QuickfirePlayRow;
        return q.session_complete === true && Boolean(q.played_at?.trim()) && isTodayLocal(q.played_at);
    }
    if (game.type !== "bullion" && game.type !== "sixes") return false;
    if (game.type === "sixes") {
        const sixesCard = game.data as SixesCard;
        const cands: (string | undefined | null)[] = [
            sixesCard.settledAt,
            sixesCard.startTime,
            sixesCard.revealedAt,
            sixesCard.createdAt,
        ];
        return cands.some((d) => d != null && String(d).trim() !== "" && isTodayLocal(String(d)));
    }
    const bullionGame = game.data as BullionGame;
    const cands: (string | undefined | null)[] = [bullionGame.startTime, bullionGame.createdAt];
    return cands.some((d) => d != null && String(d).trim() !== "" && isTodayLocal(String(d)));
}

/**
 * Quickfire in the My Games drawer: never show completed or in-progress sessions from earlier local days.
 * (Stale “open” rows can still be classified SETTLED elsewhere via {@link isGameSettled}.)
 */
function isQuickfireIncludedInMyGamesDrawer(game: CombinedGame): boolean {
    if (game.type !== "quickfire") return false;
    const q = game.data as QuickfirePlayRow;
    if (!q.played_at?.trim()) return !q.session_complete;
    return isTodayLocal(q.played_at);
}

/** Drawer: every ACTIVE row plus SETTLED rows that qualify via {@link isSettledGameMatchOrActivityTodayLocal}. */
export function filterGamesForMyGamesDrawer(games: CombinedGame[]): CombinedGame[] {
    return games.filter((g) => {
        if (g.type === "quickfire") return isQuickfireIncludedInMyGamesDrawer(g);
        return isGameActive(g) || isSettledGameMatchOrActivityTodayLocal(g);
    });
}

export function filterGamesByTab(
    games: CombinedGame[],
    tab: "ALL" | "ACTIVE" | "SETTLED" | "TODAY_YESTERDAY"
): CombinedGame[] {
    if (tab === "ALL") return games;
    if (tab === "TODAY_YESTERDAY") return filterGamesByTodayYesterday(games);
    if (tab === "ACTIVE") return games.filter(isGameActive);
    return games.filter(isGameSettled);
}

export function isTodayLocal(dateStr: string | undefined): boolean {
    if (!dateStr?.trim()) return false;
    const t = new Date(dateStr).getTime();
    if (isNaN(t)) return false;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
    return t >= todayStart && t <= todayEnd;
}

export function isTodayUtc(dateStr: string | undefined): boolean {
    if (!dateStr?.trim()) return false;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return (
        d.getUTCFullYear() === now.getUTCFullYear() &&
        d.getUTCMonth() === now.getUTCMonth() &&
        d.getUTCDate() === now.getUTCDate()
    );
}

export function hasCompletedQuickfireSessionToday(rows: QuickfirePlayRow[]): boolean {
    return rows.some((q) => q.session_complete === true && isTodayUtc(q.played_at));
}

export function hasCompletedQuickfirePlayToday(combinedGames: CombinedGame[]): boolean {
    const rows = combinedGames
        .filter((g) => g.type === "quickfire")
        .map((g) => g.data as QuickfirePlayRow);
    return hasCompletedQuickfireSessionToday(rows);
}

/** Fallback ACTIVE row when account has partial progress but `quickfire_plays` has not synced yet. */
export function buildSyntheticActiveQuickfireFromUserGames(
    games: UserGame[] | null | undefined,
): QuickfirePlayRow | null {
    if (!games?.length) return null;
    const entry = games.find((g) => g.name === "quickfire-trivia");
    if (!entry?.values) return null;
    const v = entry.values;
    const last = typeof v.last === "string" ? v.last : "";
    if (!isTodayLocal(last)) return null;
    const answered = Number(v.answered);
    const max = Number(v.questionstoanswer);
    if (!Number.isFinite(answered) || !Number.isFinite(max) || max <= 0) return null;
    if (answered <= 0 || answered >= max) return null;

    return {
        id: 0,
        game_session_id: null,
        played_at: new Date().toISOString(),
        play_name: "QUICKFIRE",
        competitions: null,
        answered_total: answered,
        max_questions: max,
        correct: 0,
        coins_won: Number(v.lastwon) || 0,
        session_complete: false,
    };
}

export function injectSyntheticActiveQuickfireIfNeeded(
    games: CombinedGame[],
    userGames: UserGame[] | null | undefined,
): CombinedGame[] {
    const hasActiveQuickfire = games.some((g) => g.type === "quickfire" && isGameActive(g));
    if (hasActiveQuickfire) return games;

    const synthetic = buildSyntheticActiveQuickfireFromUserGames(userGames);
    if (!synthetic) return games;

    const syntheticGame: CombinedGame = {
        type: "quickfire",
        date: synthetic.played_at,
        data: synthetic,
    };
    return [...games, syntheticGame].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
}

export function filterGamesByTodayYesterday(games: CombinedGame[]): CombinedGame[] {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
    const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0).getTime();
    const yesterdayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999).getTime();
    return games.filter((g) => {
        const t = new Date(g.date).getTime();
        if (isNaN(t)) return false;
        return (t >= yesterdayStart && t <= yesterdayEnd) || (t >= todayStart && t <= todayEnd);
    });
}

/** Rows whose {@link CombinedGame.date} falls on the user's local calendar today (My Games drawer). */
export function filterGamesByLocalToday(games: CombinedGame[]): CombinedGame[] {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
    return games.filter((g) => {
        const t = new Date(g.date).getTime();
        if (isNaN(t)) return false;
        return t >= todayStart && t <= todayEnd;
    });
}

export function getCachedHistory(
    activeTab: "ALL" | "ACTIVE" | "SETTLED" | "TODAY_YESTERDAY",
    gameTypeFilter: string,
    _currentPage: number,
    perPage: number
): { allGames: CombinedGame[]; totalPages: number; fullList?: CombinedGame[] } | null {
    try {
        const raw = localStorage.getItem(getHistoryCacheKey(activeTab, gameTypeFilter));
        if (!raw) return null;
        const { allGames, timestamp } = JSON.parse(raw);
        if (Date.now() - (timestamp || 0) > HISTORY_CACHE_TTL_MS) return null;
        if (!Array.isArray(allGames)) return null;
        const filtered = activeTab === "ALL" ? allGames : filterGamesByTab(allGames, activeTab);
        const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
        return { allGames: filtered, totalPages, fullList: allGames };
    } catch {
        return null;
    }
}

export function setCachedHistory(
    activeTab: "ALL" | "ACTIVE" | "SETTLED" | "TODAY_YESTERDAY",
    gameTypeFilter: string,
    _currentPage: number,
    allGames: CombinedGame[]
) {
    try {
        localStorage.setItem(
            getHistoryCacheKey(activeTab, gameTypeFilter),
            JSON.stringify({ allGames, timestamp: Date.now() }),
        );
    } catch (e) {
        console.warn("Failed to cache history", e);
    }
}

export function clearMyGamesPageCache() {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(HISTORY_CACHE_KEY_PREFIX)) keysToRemove.push(key);
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
        // ignore
    }
}

export function formatDateHeader(date: Date): string {
    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

    const dayName = days[date.getDay()];
    const dayNum = date.getDate();
    const month = months[date.getMonth()];

    return `${dayName} ${dayNum}${quickfireOrdinal(dayNum)} ${month}`;
}

const TRADER_SUMMARY_MONTHS = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
] as const;

/**
 * Match start / kickoff for My Zone trader summary header (e.g. "12 DEC 2026").
 * Prefers fixture start from market details, then trade timestamps.
 */
export function formatTraderMatchHeroDate(trade: TradeGame): string | null {
    const raw =
        trade.marketdetails?.start ??
        trade.start ??
        (trade as { "starts-at"?: string })["starts-at"] ??
        trade.tstamp ??
        trade.created_at;
    if (raw == null || typeof raw !== "string" || String(raw).trim() === "") return null;
    const d = new Date(raw.trim());
    if (Number.isNaN(d.getTime())) return null;
    const day = d.getDate();
    const mon = TRADER_SUMMARY_MONTHS[d.getMonth()];
    const y = d.getFullYear();
    return `${day} ${mon} ${y}`;
}

/**
 * Match start for My Zone squares summary header (e.g. "12 DEC 2026").
 * Prefers fixture kickoff fields, then created_at.
 */
export function formatSquaresMatchHeroDate(square: SquareGame): string | null {
    const raw = square.start_time ?? square.actual_start ?? square.start ?? square.created_at;
    if (raw == null || typeof raw !== "string" || String(raw).trim() === "") return null;
    const d = new Date(raw.trim());
    if (Number.isNaN(d.getTime())) return null;
    const day = d.getDate();
    const mon = TRADER_SUMMARY_MONTHS[d.getMonth()];
    const y = d.getFullYear();
    return `${day} ${mon} ${y}`;
}

/** Settled / revealed Sixes card date for summary header (e.g. "12 DEC 2026"). */
export function formatSixesSummaryHeaderDate(
    card: Pick<SixesCard, "settledAt" | "revealedAt" | "startTime" | "createdAt">,
): string | null {
    const raw = card.settledAt ?? card.revealedAt ?? card.startTime ?? card.createdAt;
    if (raw == null || String(raw).trim() === "") return null;
    const d = new Date(String(raw).trim());
    if (Number.isNaN(d.getTime())) return null;
    const day = d.getDate();
    const mon = TRADER_SUMMARY_MONTHS[d.getMonth()];
    const y = d.getFullYear();
    return `${day} ${mon} ${y}`;
}
